import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@amzn/stencil-react-components/modal";
import { Text } from "@amzn/stencil-react-components/text";
import { FormattedMessage } from "react-intl";

import { publishSecurityKeyIssuesChatSupportClickedMetrics } from "@/apis/Metrics";

import vendITImage from "../../assets/images/vendIT.png";
import { ReportFormData } from "./ReportSecurityKeyIssuesForm";

/**
 * Props for the ReportSecurityKeyIssuesSubmission component.
 */
interface ReportSecurityKeyIssuesSubmissionProps {
  /** The form data that was submitted */
  formData: ReportFormData | null;
  /** Callback fired when user wants to close the modal */
  onClose: () => void;
  /** Optional callback to open chat support */
  onOpenChat?: () => void;
}

/**
 * Submission confirmation screen for security key issues report.
 * Component that displays a submission confirmation screen after reporting security key issues.
 * Shows different content based on the selected delivery method (vending machine or chat with IT Support).
 *
 * @param props - The component props
 * @param props.formData - The form data that was submitted containing delivery method selection
 * @param props.onClose - Callback function fired when user wants to close the modal
 * @param props.onOpenChat - Optional callback function to open chat support
 * @returns JSX element containing the confirmation screen UI, or null if no form data
 */
export default function ReportSecurityKeyIssuesSubmission({
  formData,
  onClose,
  onOpenChat,
}: ReportSecurityKeyIssuesSubmissionProps): JSX.Element | null {
  if (!formData) {
    return null;
  }

  const isVendingMachine = formData.selectedDelivery === "it_vending_machine";
  const isChatWithIT = formData.selectedDelivery === "chat_with_it";

  /**
   * Handles the "Chat with IT Support" button click.
   *
   * Publishes metrics for user interaction, opens the chat support interface
   * if available, and closes the modal to allow the user to interact with
   * the chat support system.
   */
  const handleChatWithIT = () => {
    publishSecurityKeyIssuesChatSupportClickedMetrics();
    onOpenChat?.();
    onClose();
  };

  return (
    <>
      <ModalHeader>
        <Text fontSize="T300" fontWeight="bold">
          <FormattedMessage id="insertAndTestSecurityKey-issuesSubmission-title" />
        </Text>
      </ModalHeader>
      <ModalBody>
        <View padding="S800">
          {isVendingMachine && (
            <Col rowGap="S400" alignItems="center">
              <Text>
                <FormattedMessage id="insertAndTestSecurityKey-issuesSubmission-vendingMachine-instructions" />
              </Text>

              <View>
                <img
                  src={vendITImage}
                  alt="IT Vending Machine"
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </View>
            </Col>
          )}

          {isChatWithIT && (
            <Text>
              <FormattedMessage id="insertAndTestSecurityKey-issuesSubmission-chatWithIT-instructions" />
            </Text>
          )}
        </View>
      </ModalBody>
      <ModalFooter>
        <Row justifyContent="flex-end" width="100%">
          {isVendingMachine && (
            <Button onClick={onClose} variant={ButtonVariant.Primary}>
              <FormattedMessage id="insertAndTestSecurityKey-issuesSubmission-closeButton" />
            </Button>
          )}
          {isChatWithIT && (
            <Button onClick={handleChatWithIT} variant={ButtonVariant.Primary}>
              <FormattedMessage id="text-chatWithITSupport" />
            </Button>
          )}
        </Row>
      </ModalFooter>
    </>
  );
}
